import { useEffect, useRef } from 'react';
import debounce from 'lodash/debounce';

import type { EditorView } from '@atlaskit/editor-prosemirror/view';

import { useMeetingSignalExperiment } from '@confluence/experiment-meeting-signal/entry-points/useMeetingSignalExperiment.ts';
import { adfToPlainText } from '@confluence/experiment-meeting-signal-core/entry-points/adfToPlainText.ts';
import type { MeetingSignalInput } from '@confluence/experiment-meeting-signal-core/entry-points/meetingSignalTypes.ts';
import { useMeetingSignalDetection } from '@confluence/experiment-meeting-signal-instrumentation/entry-points/useMeetingSignalDetection.ts';

import { useDocChangeContext } from './DocChangeContext.tsx';
import type { RetrieveEditorTitleAndContent } from './hooks/useRetrieveEditorTitleAndContent.ts';

// The body editor has no blur hook, so content edits are evaluated on a debounce
// off doc changes. 1000 ms per the spec (STARB-3763); the detector's fire-once
// guard swallows the repeated debounced emits so at most one contentEdit event
// fires per editor mount.
const CONTENT_DEBOUNCE_MS = 1000;

export interface MeetingSignalEditorDetectionParams {
	/** True for live pages, which open in the editor rather than the view renderer. */
	isLivePage: boolean;
	/** Retrieves the loaded title + ADF body from the collab provider (live pages). */
	retrieveEditorTitleAndContent: RetrieveEditorTitleAndContent;
}

/**
 * Meeting-signal instrumentation (STARB-3763) for the editor surfaces. Two
 * independent triggers, each with its own fire-once guard (via a separate
 * `useMeetingSignalDetection` instance) so both can fire once per mount:
 *
 * 1. **contentEdit** (`editPageScreen`) — for every full-page editor (regular
 *    and live): subscribes to `DocChangeContext` and, debounced, matches the
 *    body plain text.
 * 2. **livePageOpen** (`viewPageScreen`) — **live pages only**: a live page opens
 *    in the editor, not the view renderer, so the content-renderer `pageOpen`
 *    surface never fires for it. This fills that gap — once the collab content
 *    is available, it reads the loaded title + body and evaluates them on open,
 *    giving live pages the same on-open coverage as regular pages (no edit
 *    needed).
 *
 * Pure instrumentation — no behaviour change. Must be called from a component
 * inside a `DocChangeContext` provider (e.g. wrapped with `withDocChangeContext`).
 */
export const useMeetingSignalEditorDetection = ({
	isLivePage,
	retrieveEditorTitleAndContent,
}: MeetingSignalEditorDetectionParams): void => {
	const { subscribeDocChange, unsubscribeDocChange } = useDocChangeContext();
	const detectContentEdit = useMeetingSignalDetection();
	const detectLiveOpen = useMeetingSignalDetection();
	const { evaluate: evaluateMeetingSignalExperimentContentEdit } = useMeetingSignalExperiment();
	const { evaluate: evaluateMeetingSignalExperimentLivePageOpen } = useMeetingSignalExperiment();

	// Trigger 1: content-edit, debounced off doc changes.
	useEffect(() => {
		const handleDocChange = debounce((editorView: EditorView) => {
			const doc = editorView?.state?.doc;
			if (!doc) {
				return;
			}
			// ProseMirror gives us plain text directly — no ADF encoding needed.
			const contentText = doc.textBetween(0, doc.content.size, '\n', ' ');
			const signal = {
				source: 'editPageScreen',
				trigger: 'contentEdit',
				contentText,
			} satisfies MeetingSignalInput;
			detectContentEdit(signal);
			evaluateMeetingSignalExperimentContentEdit(signal);
		}, CONTENT_DEBOUNCE_MS);

		subscribeDocChange(handleDocChange);
		return () => {
			handleDocChange.cancel();
			unsubscribeDocChange(handleDocChange);
		};
	}, [
		subscribeDocChange,
		unsubscribeDocChange,
		detectContentEdit,
		evaluateMeetingSignalExperimentContentEdit,
	]);

	// Trigger 2: live-page open. Retry (via the effect's deps) until the first
	// successful retrieval, then stop; retrieval errors are ignored (best-effort
	// instrumentation).
	const liveOpenDoneRef = useRef(false);
	useEffect(() => {
		if (!isLivePage || liveOpenDoneRef.current) {
			return;
		}
		let cancelled = false;
		retrieveEditorTitleAndContent()
			.then(({ title, adf }) => {
				if (cancelled) {
					return;
				}
				liveOpenDoneRef.current = true;
				const signal = {
					source: 'viewPageScreen',
					trigger: 'livePageOpen',
					titleText: title,
					contentText: adfToPlainText(adf as unknown as Record<string, unknown>),
				} satisfies MeetingSignalInput;
				detectLiveOpen(signal);
				evaluateMeetingSignalExperimentLivePageOpen(signal);
			})
			.catch(() => {
				// Best-effort instrumentation — ignore collab/ack retrieval errors.
			});
		return () => {
			cancelled = true;
		};
	}, [
		isLivePage,
		retrieveEditorTitleAndContent,
		detectLiveOpen,
		evaluateMeetingSignalExperimentLivePageOpen,
	]);
};
